import React, { useCallback, useEffect, useState } from "react";
import { Radar, RefreshCw, Plus, CircleCheck, CircleAlert, Clock, Loader2, PauseCircle, Timer, Trash2 } from "lucide-react";
import { monitoredAreasApi, apiErrorMessage } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { RefreshVenuesForm } from "@/components/RefreshVenues";
import { EmptyState } from "@/components/EmptyState";
import { Sheet } from "@/components/Sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const STATUS_META = {
  healthy: { icon: CircleCheck, cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Healthy" },
  failing: { icon: CircleAlert, cls: "bg-rose-50 text-rose-700 border-rose-200", label: "Failing" },
  scanning: { icon: Loader2, cls: "bg-sky-50 text-sky-700 border-sky-200", label: "Scanning", spin: true },
  pending: { icon: Clock, cls: "bg-amber-50 text-amber-700 border-amber-200", label: "Pending" },
  paused: { icon: PauseCircle, cls: "bg-slate-100 text-slate-500 border-slate-200", label: "Paused" },
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
const fmtCadence = (m) => (m % 60 === 0 ? `${m / 60}h` : `${m}m`);

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.pending;
  const Icon = m.icon;
  return (
    <span data-testid={`area-status-${status}`} className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-semibold ${m.cls}`}>
      <Icon className={`h-3 w-3 ${m.spin ? "animate-spin" : ""}`} /> {m.label}
    </span>
  );
}

export default function MonitoredAreas() {
  const { user } = useAuth() || {};
  const isAdmin = ["admin", "super_admin"].includes(user?.role);
  const [data, setData] = useState(null);
  const [scanning, setScanning] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);

  const load = useCallback(async (silent = false) => {
    try {
      const { data } = await monitoredAreasApi.health();
      setData(data);
    } catch (e) { if (!silent) toast.error(apiErrorMessage(e)); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => load(true), 30000); // reflect background scans
    return () => clearInterval(t);
  }, [load]);

  const scan = async (id) => {
    setScanning(id);
    try {
      const { data } = await monitoredAreasApi.scan(id);
      toast.success(`Scan complete · ${data.new_caterctrl_prospects} new prospect(s)`);
      load(true);
    } catch (e) {
      if (e?.response?.data?.error?.code === "SCAN_IN_PROGRESS") toast.info("A scan is already running for this area.");
      else toast.error(apiErrorMessage(e));
    } finally { setScanning(null); }
  };

  const s = data?.summary;

  const confirmRemove = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    try {
      await monitoredAreasApi.remove(removeTarget.id);
      toast.success(`Removed “${removeTarget.name}” — future scans stopped`);
      setRemoveTarget(null);
      load(true);
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setRemoving(false); }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-5 md:py-8" data-testid="monitored-areas-page">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-xl font-extrabold tracking-tight text-slate-900 md:text-2xl">Monitored Areas</h1>
          <p className="mt-0.5 text-xs text-slate-400">Scanned automatically in the background every {data ? fmtCadence(data.scheduler.default_cadence_minutes) : "…"} · checks run every {data ? data.scheduler.tick_seconds : 60}s</p>
        </div>
        <button data-testid="area-create-btn" onClick={() => setCreateOpen(true)} className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-teal-700 px-3 text-sm font-semibold text-white hover:bg-teal-800"><Plus className="h-4 w-4" /> Add area</button>
      </div>

      {isAdmin && <RefreshVenuesForm />}

      {/* Health rollup */}
      {s && s.total > 0 && (
        <div className="mb-4 grid grid-cols-3 gap-2 sm:grid-cols-5" data-testid="areas-summary">
          <SummaryStat label="Total" value={s.total} />
          <SummaryStat label="Healthy" value={s.healthy} cls="text-emerald-600" />
          <SummaryStat label="Failing" value={s.failing} cls="text-rose-600" testid="areas-failing-count" />
          <SummaryStat label="Scanning" value={s.scanning} cls="text-sky-600" />
          <SummaryStat label="Pending" value={s.pending} cls="text-amber-600" />
        </div>
      )}

      {!data ? (
        <div className="h-40 animate-pulse rounded-xl border border-slate-200 bg-white" />
      ) : data.items.length === 0 ? (
        <EmptyState testid="areas-empty" icon={Radar} title="No monitored areas yet"
          description="Add an area to have LeadCTRL scan the FSA register for new CaterCTRL prospects on a schedule." />
      ) : (
        <div className="space-y-2.5" data-testid="areas-list">
          {data.items.map((a) => (
            <div key={a.id} data-testid={`area-row-${a.id}`} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate font-heading text-sm font-bold text-slate-800">{a.name}</p>
                  <p className="mt-0.5 text-xs capitalize text-slate-400">{a.kind.replace(/_/g, " ")} · every {fmtCadence(a.scan_cadence_minutes)}</p>
                </div>
                <StatusBadge status={a.status} />
              </div>

              <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs sm:grid-cols-4">
                <HealthCell label="Last scan" value={fmt(a.last_scan_at)} />
                <HealthCell label="Last success" value={fmt(a.last_success_at)} />
                <HealthCell icon={Timer} label="Next scan" value={fmt(a.next_scan_at)} />
                <HealthCell label="Failures" value={a.consecutive_failure_count} danger={a.consecutive_failure_count > 0} />
              </div>

              {a.status === "failing" && a.last_failure_reason && (
                <p data-testid={`area-failure-${a.id}`} className="mt-2 rounded-md bg-rose-50 px-2 py-1 text-[11px] font-medium text-rose-600">{a.last_failure_reason}</p>
              )}

              <div className="mt-3 flex justify-end gap-2">
                <button data-testid={`area-remove-${a.id}`} onClick={() => setRemoveTarget(a)}
                  className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-rose-200 bg-white px-3 text-xs font-semibold text-rose-600 hover:bg-rose-50">
                  <Trash2 className="h-3.5 w-3.5" /> Remove
                </button>
                <button data-testid={`area-scan-${a.id}`} onClick={() => scan(a.id)} disabled={scanning === a.id || a.status === "scanning"}
                  className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-60">
                  <RefreshCw className={`h-3.5 w-3.5 ${scanning === a.id ? "animate-spin" : ""}`} /> Scan now
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <CreateAreaSheet open={createOpen} onClose={() => setCreateOpen(false)} onCreated={() => load(true)} />

      <AlertDialog open={!!removeTarget} onOpenChange={(v) => !v && setRemoveTarget(null)}>
        <AlertDialogContent data-testid="area-remove-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove monitored area {removeTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Future automatic FSA scans in this area will stop. Prospects, activities and history
              already created will remain untouched. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="area-remove-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="area-remove-confirm" onClick={confirmRemove} disabled={removing}
              className="bg-rose-600 hover:bg-rose-700">
              {removing ? "Removing…" : "Remove area"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SummaryStat({ label, value, cls = "text-slate-800", testid }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-2 text-center" data-testid={testid}>
      <p className={`text-lg font-bold ${cls}`}>{value ?? 0}</p>
      <p className="text-[10px] uppercase tracking-wide text-slate-400">{label}</p>
    </div>
  );
}

function HealthCell({ icon: Icon, label, value, danger }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`mt-0.5 font-semibold ${danger ? "text-rose-600" : "text-slate-700"}`}>
        {Icon && <Icon className="mr-1 inline h-3 w-3 text-slate-400" />}{value}
      </p>
    </div>
  );
}

function CreateAreaSheet({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", postcode: "", radius_km: 2, scan_cadence_minutes: 360 });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      await monitoredAreasApi.create({
        name: form.name, kind: "postcode_radius",
        parameters: { postcode: form.postcode, radius_km: Number(form.radius_km) },
        scan_cadence_minutes: Number(form.scan_cadence_minutes),
      });
      toast.success("Area added — first scan will run shortly");
      onClose(); onCreated();
      setForm({ name: "", postcode: "", radius_km: 2, scan_cadence_minutes: 360 });
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <Sheet open={open} onClose={onClose} title="Add monitored area" testid="area-create-sheet">
      <div className="space-y-3">
        <Field label="Name"><input data-testid="area-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Bristol city centre" className="min-h-[44px] w-full rounded-lg border border-slate-200 px-3 text-sm" /></Field>
        <Field label="Postcode"><input data-testid="area-postcode-input" value={form.postcode} onChange={(e) => set("postcode", e.target.value.toUpperCase())} placeholder="BS1 4DJ" className="min-h-[44px] w-full rounded-lg border border-slate-200 px-3 text-sm" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Radius (km)"><input data-testid="area-radius-input" type="number" min={0.5} step={0.5} value={form.radius_km} onChange={(e) => set("radius_km", e.target.value)} className="min-h-[44px] w-full rounded-lg border border-slate-200 px-3 text-sm" /></Field>
          <Field label="Scan cadence">
            <select data-testid="area-cadence-select" value={form.scan_cadence_minutes} onChange={(e) => set("scan_cadence_minutes", e.target.value)} className="min-h-[44px] w-full rounded-lg border border-slate-200 px-3 text-sm">
              <option value={60}>Every hour</option>
              <option value={360}>Every 6 hours</option>
              <option value={720}>Every 12 hours</option>
              <option value={1440}>Daily</option>
            </select>
          </Field>
        </div>
        <button data-testid="area-create-submit" onClick={submit} disabled={busy || !form.name || !form.postcode}
          className="min-h-[48px] w-full rounded-lg bg-teal-700 font-semibold text-white hover:bg-teal-800 disabled:opacity-60">
          {busy ? "Adding…" : "Add area"}
        </button>
      </div>
    </Sheet>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</span>
      {children}
    </label>
  );
}
